import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Card,
  CardContent,
  CardActions,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { 
  PersonAdd, 
  Payment, 
  Cancel, 
  CheckCircle, 
  Error,
  CreditCard,
  AccountBalance
} from '@mui/icons-material'
import { CompanyService } from '../../services/CompanyService'
import { billingService } from '../../services/BillingService'
import { useAppSelector } from '../../hooks/useAppSelector'
import { useRbacStore } from '../../stores/rbacStore'
import { usePermission } from '../../hooks/usePermission'
import { memberRoleLabel } from '../CompanyManagement/utils/roleOptions'

interface PendingUser {
  id: string
  user_email: string
  user_name: string
  role: string
  /** The key and the Role's current name; an old cloud sends neither (`memberRoleLabel`). */
  roleKey?: string | null
  role_name?: string | null
  effective_role_key?: string | null
  role_is_default?: boolean
  department?: string
  title?: string
  billing_amount: number // Full monthly amount
  prorated_amount?: number // Prorated amount for current billing period
  billing_cycle: string
  created_at: string
}

interface PendingUserManagerProps {
  open: boolean
  onClose: () => void
  companyId: string
  onUsersAdded: () => void
}

export const PendingUserManager: React.FC<PendingUserManagerProps> = ({
  open,
  onClose,
  companyId,
  onUsersAdded,
}) => {
  const [loading, setLoading] = useState(false)
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([])
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [paymentMethods, setPaymentMethods] = useState<any[]>([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('')
  const [showPaymentOptions, setShowPaymentOptions] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalAmount, setTotalAmount] = useState(0)
  const [creditBalance, setCreditBalance] = useState(0)
  const [useCredits, setUseCredits] = useState(true)
  const [billingAccount, setBillingAccount] = useState<any>(null)
  // The credit balance could not be read (refused or failed): the zero shown is not a real balance.
  const [creditUnavailable, setCreditUnavailable] = useState(false)
  // The addition preview (the prorated amounts) was refused or failed. The monthly amount is not
  // what is charged now, so no total is shown and Process stays off until the preview loads.
  const [previewUnavailable, setPreviewUnavailable] = useState(false)
  
  // Get current user from Redux store
  const userData = useAppSelector((state) => state.user.userData)
  // Names the screen already holds, for rows that carry a key but no name; never fetched here.
  const knownRoles = useRbacStore((s) => s.catalogue?.systemRoles)
  // The credit balance is billing.view_plan data: without it nothing about credits is shown and
  // the call is not made, because cloud would refuse it (SC-439, phase F).
  const canViewPlan = usePermission('billing.view_plan')

  useEffect(() => {
    if (open) {
      // Clears on close already, but companyId can also change while open — a different
      // company is a different subject, so drop the previous rows first. [SC-313]
      setPendingUsers([])
      loadPendingUsers()
      loadPaymentMethods()
      loadBillingAccount()
    } else {
      // Reset state when dialog closes
      setPendingUsers([])
      setSelectedUsers([])
      setShowPaymentOptions(false)
      setShowConfirmation(false)
      setSelectedPaymentMethod('')
      setError(null)
      setCreditUnavailable(false)
      setPreviewUnavailable(false)
      setTotalAmount(0)
    }
  }, [open, companyId])

  // One credit read per open (and per company), and only for people who may see it. The balance
  // is reset first so a previous company's credits never show or apply here.
  useEffect(() => {
    setCreditBalance(0)
    setCreditUnavailable(false)
    if (open && canViewPlan) loadCreditBalance()
  }, [open, companyId, canViewPlan])

  useEffect(() => {
    // Calculate total prorated amount for selected users
    const total = pendingUsers
      .filter(user => selectedUsers.includes(user.id))
      .reduce((sum, user) => sum + (user.prorated_amount || user.billing_amount), 0)
    setTotalAmount(total)
  }, [selectedUsers, pendingUsers])

  const loadPendingUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      setPreviewUnavailable(false)
      const result = await CompanyService.getPendingUsers(companyId)
      if (result.success) {
        const pendingUsers = result.pending_users || []
        
        // Get billing impact preview to calculate correct prorated amounts
        const billingPreview = await CompanyService.getBillingImpactPreview(companyId, 'addition', pendingUsers.length)
        
        if (billingPreview.success && billingPreview.billing_impact) {
          // Calculate prorated amount per user
          const proratedAmountPerUser = billingPreview.billing_impact.prorated_amount / pendingUsers.length
          
          // Update each pending user with the correct prorated amount
          const usersWithProratedAmounts = pendingUsers.map((user: PendingUser) => ({
            ...user,
            prorated_amount: proratedAmountPerUser
          }))
          
          setPendingUsers(usersWithProratedAmounts)
        } else {
          // The rows stay (they can still be cancelled), but without the preview they would be
          // charged at the full monthly amount in the total, which is not what the payment takes.
          setPendingUsers(pendingUsers)
          setPreviewUnavailable(true)
        }
      } else {
        setError(result.error || 'Failed to load pending users')
      }
    } catch (error) {
      console.error('Error loading pending users:', error)
      setError('Failed to load pending users')
    } finally {
      setLoading(false)
    }
  }

  const loadPaymentMethods = async () => {
    try {
      const result = await billingService.getCompanyPaymentMethods(companyId)
      if (result.success) {
        setPaymentMethods(result.payment_methods || [])
        // Auto-select default payment method if available
        const defaultMethod = result.payment_methods?.find(pm => pm.is_default)
        if (defaultMethod) {
          setSelectedPaymentMethod(defaultMethod.id)
        }
      }
    } catch (error) {
      console.error('Error loading payment methods:', error)
    }
  }

  const loadCreditBalance = async () => {
    if (!canViewPlan) return
    try {
      const result = await CompanyService.getCompanyCreditInfo(companyId)
      if (result.success) {
        setCreditBalance(result.credit_info?.current_balance || 0)
        setCreditUnavailable(false)
      } else {
        setCreditUnavailable(true)
      }
    } catch (error) {
      setCreditUnavailable(true)
      console.error('Error loading credit balance:', error)
    }
  }

  const loadBillingAccount = async () => {
    try {
      const result = await billingService.getCompanyBillingAccount(companyId)
      if (result.success) {
        setBillingAccount(result.billing_account)
      }
    } catch (error) {
      console.error('Error loading billing account:', error)
    }
  }

  const handleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const handleSelectAll = () => {
    if (selectedUsers.length === pendingUsers.length) {
      setSelectedUsers([])
    } else {
      setSelectedUsers(pendingUsers.map(user => user.id))
    }
  }

  const handleCancelUser = async (userId: string) => {
    try {
      setLoading(true)
      const result = await CompanyService.cancelPendingUser(userId)
      if (result.success) {
        await loadPendingUsers()
        setSelectedUsers(prev => prev.filter(id => id !== userId))
      } else {
        setError(result.error || 'Failed to cancel pending user')
      }
    } catch (error) {
      console.error('Error cancelling pending user:', error)
      setError('Failed to cancel pending user')
    } finally {
      setLoading(false)
    }
  }

  const handleProcessPayment = async () => {
    if (selectedUsers.length === 0) {
      setError('Please select at least one user to process')
      return
    }
    if (previewUnavailable) return

    // Check if user is authenticated
    if (!userData?.id) {
      setError('User authentication required. Please log in again.')
      return
    }

    try {
      setProcessing(true)
      setError(null)

      const remainingAfterCredits = Math.max(0, totalAmount - (useCredits ? creditBalance : 0))
      const requiresPayment = remainingAfterCredits > 0
      const hasPaymentMethods = paymentMethods.length > 0

      // Always require payment method selection if we have saved payment methods
      // This ensures the backend can use the payment method even for small amounts
      if (requiresPayment || hasPaymentMethods) {
        // Step 1: Show payment options if not already shown
        if (!showPaymentOptions) {
          setShowPaymentOptions(true)
          setProcessing(false)
          return
        }

        // Step 2: Show payment method selection if not confirmed
        if (showPaymentOptions && !showConfirmation) {
          setShowConfirmation(true)
          setProcessing(false)
          return
        }

        // Step 3: Process payment
        if (showPaymentOptions && showConfirmation) {
          // Always require payment method selection if we have saved payment methods
          if (paymentMethods.length > 0 && !selectedPaymentMethod) {
            setError('Please select a payment method')
            setLoading(false)
            return
          }
          
          // Process payment with saved payment method (like BillingTierSelector)
          console.log('Processing payment with:', {
            selectedUsers,
            selectedPaymentMethod,
            useCredits,
            totalAmount,
            creditBalance,
            remainingAfterCredits
          });
          
          const result = await CompanyService.processPendingUserPayment(companyId, {
            pending_user_ids: selectedUsers,
            payment_method_id: selectedPaymentMethod, // Always pass this if we have payment methods
            use_credits: useCredits,
            created_by: userData?.id,
            success_url: `${window.location.origin}/billing/pending-users/success`,
            cancel_url: `${window.location.origin}/billing/pending-users/cancel`
          })

          if (result.success) {
            await loadPendingUsers()
            await loadCreditBalance()
            setSelectedUsers([])
            setShowPaymentOptions(false)
            setShowConfirmation(false)
            onUsersAdded()
            onClose()
          } else {
            setError(result.error || 'Failed to process payment')
          }
        }
      } else {
        // No payment required, just process with credits
        console.log('Processing with credits only, no payment required')
        console.log('Selected users:', selectedUsers)
        console.log('Use credits:', useCredits)
        console.log('Selected payment method:', selectedPaymentMethod)
        
        const result = await CompanyService.processPendingUserPayment(companyId, {
          pending_user_ids: selectedUsers,
          use_credits: useCredits,
          payment_method_id: selectedPaymentMethod, // Pass payment method even if we think no payment is needed
          created_by: userData?.id,
          success_url: `${window.location.origin}/billing/pending-users/success`,
          cancel_url: `${window.location.origin}/billing/pending-users/cancel`
        })

        console.log('processPendingUserPayment result:', result)

        if (result.success) {
          console.log('Payment processing successful, refreshing data...')
          await loadPendingUsers()
          await loadCreditBalance()
          setSelectedUsers([])
          setShowPaymentOptions(false)
          setShowConfirmation(false)
          onUsersAdded()
          onClose()
        } else {
          console.error('Payment processing failed:', result.error)
          setError(result.error || 'Failed to process payment')
        }
      }
    } catch (error: any) {
      console.error('Error processing payment:', error)
      setError(error.message || 'Failed to process payment')
    } finally {
      setProcessing(false)
    }
  }

  const getRoleColor = (role: string) => {
    switch ((role || '').toLowerCase()) {
      case 'admin':
        return 'var(--sevv-error, #e74c3c)'
      case 'manager':
        return 'var(--sevv-warning, #f39c12)'
      case 'user':
        return 'var(--sevv-success, #27ae60)'
      default:
        return 'var(--sevv-gray-400, #bdbdbd)'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  // Helper function to get the action button text and icon
  const getActionButtonProps = () => {
    if (previewUnavailable) {
      return { text: 'Amount unavailable', icon: null, disabled: true }
    }
    if (selectedUsers.length === 0) {
      return { text: 'Select Users', icon: null, disabled: true }
    }

    const remainingAfterCredits = Math.max(0, totalAmount - (useCredits ? creditBalance : 0))
    const requiresPayment = remainingAfterCredits > 0
    const hasPaymentMethods = paymentMethods.length > 0

    if (requiresPayment || hasPaymentMethods) {
      if (showPaymentOptions && showConfirmation) {
        // Final confirmation step
        return { text: 'Confirm Payment', icon: <Payment />, disabled: false }
      } else if (showPaymentOptions && !showConfirmation) {
        // Payment method selection step
        return { text: 'Continue to Payment', icon: <Payment />, disabled: false }
      } else {
        // Initial step
        return { text: 'Set Up Payment', icon: <Payment />, disabled: false }
      }
    } else {
      return { text: 'Process with Credits', icon: <CheckCircle />, disabled: false }
    }
  }

  const remainingAfterCredits = Math.max(0, totalAmount - (useCredits ? creditBalance : 0))

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      PaperProps={{
        sx: {
          background: 'var(--sevv-bg-paper, #ffffff)',
          border: '1px solid var(--sevv-divider, #eeeeee)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          borderRadius: '16px',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: 'var(--sevv-bg-default, #fafafa)', 
        color: 'var(--sevv-text-primary, #424242)',
        borderBottom: '1px solid var(--sevv-divider, #eeeeee)',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 2
      }}>
        <PersonAdd sx={{ color: 'var(--sevv-primary, #f97415)' }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--sevv-text-primary, #424242)' }}>
            Pending User Additions
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--sevv-text-secondary, #757575)', mt: 0.5 }}>
            {pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} waiting for payment
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 2, overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, p: 1.5 }}>
            {error}
          </Alert>
        )}

        {/* Spinner only while there is nothing to show. Gating on the loading flag
            alone replaced an already-populated list with a spinner on every
            refetch, which read as the page reloading itself. [SC-313] */}
        {previewUnavailable && pendingUsers.length > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 2, p: 1.5 }}
            action={
              <Button color="inherit" size="small" onClick={loadPendingUsers} disabled={loading}>
                Retry
              </Button>
            }
          >
            The amount due now could not be loaded, so payment is switched off until it loads.
          </Alert>
        )}

        {canViewPlan && creditUnavailable && (
          <Alert severity="warning" sx={{ mb: 2, p: 1.5 }}>
            Your credit balance could not be loaded, so credits are not applied here.
          </Alert>
        )}

        {loading && pendingUsers.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress sx={{ color: 'var(--sevv-primary, #f97415)' }} />
          </Box>
        ) : pendingUsers.length === 0 && error ? (
          // The list failed to load (refused or error): the alert above says why. "All users have
          // been processed" would be false, since nothing was read.
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" sx={{ color: 'var(--sevv-text-secondary, #757575)' }}>
              Pending users could not be loaded.
            </Typography>
          </Box>
        ) : pendingUsers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircle sx={{ fontSize: 48, color: 'var(--sevv-success, #27ae60)', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'var(--sevv-text-primary, #424242)', mb: 1 }}>
              No Pending Users
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--sevv-text-secondary, #757575)' }}>
              All users have been processed successfully.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Summary Card */}
            <Card sx={{ 
              mb: 3, 
              backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)', 
              border: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)' 
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: 'var(--sevv-text-primary, #424242)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalance sx={{ color: 'var(--sevv-primary, #f97415)' }} />
                    Payment Summary
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSelectAll}
                    sx={{ 
                      color: 'var(--sevv-primary, #f97415)', 
                      borderColor: 'var(--sevv-primary, #f97415)',
                      borderWidth: '2px',
                      '&:hover': {
                        borderColor: 'var(--sevv-primary-light, #ffa047)',
                        backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)',
                        borderWidth: '2px'
                      }
                    }}
                  >
                    {selectedUsers.length === pendingUsers.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: 'var(--sevv-text-secondary, #757575)' }}>
                      Selected Users
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'var(--sevv-text-primary, #424242)' }}>
                      {selectedUsers.length} of {pendingUsers.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: 'var(--sevv-text-secondary, #757575)' }}>
                      Prorated Amount (Now)
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'var(--sevv-text-primary, #424242)' }}>
                      {previewUnavailable ? '-' : formatCurrency(totalAmount)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--sevv-gray-400, #bdbdbd)' }}>
                      For remaining billing period
                    </Typography>
                  </Grid>
                </Grid>

                {canViewPlan && creditBalance > 0 && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'color-mix(in srgb, var(--sevv-success, #27ae60) 10%, transparent)', borderRadius: 1 }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={useCredits}
                          onChange={(e) => setUseCredits(e.target.checked)}
                          sx={{ color: 'var(--sevv-success, #27ae60)' }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ color: 'var(--sevv-text-primary, #424242)', fontWeight: 600 }}>
                            Use available credits: {formatCurrency(creditBalance)}
                          </Typography>
                          {useCredits && (
                            <Box sx={{ mt: 1 }}>
                              {remainingAfterCredits === 0 ? (
                                <Typography variant="caption" sx={{ color: 'var(--sevv-success, #27ae60)', fontWeight: 600 }}>
                                  ✅ Credits will cover the full amount
                                </Typography>
                              ) : (
                                <>
                                  <Typography variant="caption" sx={{ color: 'var(--sevv-text-secondary, #757575)', display: 'block' }}>
                                    Credits will cover: {formatCurrency(Math.min(creditBalance, totalAmount))}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'var(--sevv-warning, #f39c12)', fontWeight: 600, display: 'block' }}>
                                    Remaining to pay: {formatCurrency(remainingAfterCredits)}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          )}
                        </Box>
                      }
                      sx={{ color: 'var(--sevv-text-primary, #424242)' }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Pending Users List */}
            <List sx={{ backgroundColor: 'var(--sevv-bg-default, #fafafa)', borderRadius: 2, p: 1, border: '1px solid var(--sevv-divider, #eeeeee)' }}>
              {pendingUsers.map((user, index) => (
                <React.Fragment key={user.id}>
                  <ListItem
                    sx={{
                      backgroundColor: selectedUsers.includes(user.id) 
                        ? 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)' 
                        : 'transparent',
                      borderRadius: 1,
                      mb: 1,
                      border: selectedUsers.includes(user.id) 
                        ? '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)' 
                        : '1px solid transparent',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 5%, transparent)',
                        border: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)'
                      }
                    }}
                    onClick={() => handleUserSelection(user.id)}
                  >
                    <FormControlLabel
                      control={
                        <Radio
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleUserSelection(user.id)}
                          sx={{ color: 'var(--sevv-primary, #f97415)' }}
                        />
                      }
                      label=""
                      sx={{ m: 0, mr: 1 }}
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Box component="div" sx={{ color: 'var(--sevv-text-primary, #424242)', fontWeight: 500, fontSize: '1rem' }}>
                            {user.user_name || user.user_email}
                          </Box>
                          <Chip
                            label={memberRoleLabel(user, user.role, knownRoles)}
                            size="small"
                            sx={{
                              backgroundColor: getRoleColor(user.role),
                              color: '#ffffff',
                              fontSize: '0.7rem',
                              height: 20
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Box component="div" sx={{ color: 'var(--sevv-text-secondary, #757575)', fontSize: '0.875rem' }}>
                            {user.user_email}
                          </Box>
                          {user.department && (
                            <Box component="div" sx={{ color: 'var(--sevv-gray-400, #bdbdbd)', fontSize: '0.75rem' }}>
                              {user.department} • {user.title}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Box component="div" sx={{ color: 'var(--sevv-text-primary, #424242)', fontWeight: 600, fontSize: '1rem' }}>
                            {formatCurrency(user.prorated_amount || user.billing_amount)}
                          </Box>
                          {user.prorated_amount && user.prorated_amount !== user.billing_amount && (
                            <Box component="div" sx={{ color: 'var(--sevv-gray-400, #bdbdbd)', fontSize: '0.75rem' }}>
                              (Monthly: {formatCurrency(user.billing_amount)})
                            </Box>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleCancelUser(user.id)
                          }}
                          sx={{ color: 'var(--sevv-error, #e74c3c)' }}
                        >
                          <Cancel />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < pendingUsers.length - 1 && <Divider sx={{ borderColor: 'var(--sevv-divider, #eeeeee)' }} />}
                </React.Fragment>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      {/* Payment Method Selection */}
      {showPaymentOptions && showConfirmation && (
        <Box sx={{ 
          p: 2, 
          backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)', 
          borderTop: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
          borderBottom: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)'
        }}>
          <Typography variant="h6" sx={{ color: 'var(--sevv-text-primary, #424242)', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Payment sx={{ color: 'var(--sevv-primary, #f97415)' }} />
            Choose Payment Method
          </Typography>
          
          <Typography variant="body2" sx={{ color: 'var(--sevv-text-secondary, #757575)', mb: 2 }}>
            Payment is required to add these users. Select a payment method to continue.
          </Typography>
          
          {paymentMethods.length > 0 ? (
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedPaymentMethod}
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                sx={{ gap: 1 }}
              >
                {paymentMethods.map((method) => (
                  <Paper
                    key={method.id}
                    sx={{
                      p: 1.5,
                      border: selectedPaymentMethod === method.id ? '2px solid var(--sevv-primary, #f97415)' : '1px solid var(--sevv-divider, #eeeeee)',
                      backgroundColor: selectedPaymentMethod === method.id ? 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)' : 'var(--sevv-bg-paper, #ffffff)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      borderRadius: 1,
                    }}
                    onClick={() => setSelectedPaymentMethod(method.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        value={method.id}
                        control={<Radio size="small" />}
                        label=""
                        sx={{ m: 0 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        {method.card && (
                          <>
                            <Typography variant="body2" sx={{ color: 'var(--sevv-text-primary, #424242)', fontWeight: 500 }}>
                              {method.card.brand.toUpperCase()} •••• {method.card.last4}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--sevv-text-secondary, #757575)' }}>
                              Expires {method.card.exp_month}/{method.card.exp_year}
                              {method.is_default && ' • Default'}
                            </Typography>
                          </>
                        )}
                        {method.us_bank_account && (
                          <>
                            <Typography variant="body2" sx={{ color: 'var(--sevv-text-primary, #424242)', fontWeight: 500 }}>
                              {method.us_bank_account.bank_name || 'Bank Account'} •••• {method.us_bank_account.last4}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--sevv-text-secondary, #757575)' }}>
                              {method.us_bank_account.account_type} account
                              {method.is_default && ' • Default'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </RadioGroup>
            </FormControl>
          ) : (
            <Typography variant="body2" sx={{ color: 'var(--sevv-text-secondary, #757575)', mb: 1 }}>
              No saved payment methods found. You'll be redirected to add a new payment method.
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowConfirmation(false)}
              sx={{ 
                color: 'var(--sevv-primary, #f97415)', 
                borderColor: 'var(--sevv-primary, #f97415)',
                borderWidth: '2px',
                '&:hover': {
                  borderColor: 'var(--sevv-primary-light, #ffa047)',
                  backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)',
                  borderWidth: '2px'
                }
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <Box sx={{ 
          p: 2, 
          backgroundColor: 'color-mix(in srgb, var(--sevv-warning, #f39c12) 10%, transparent)', 
          borderTop: '1px solid color-mix(in srgb, var(--sevv-warning, #f39c12) 30%, transparent)',
          borderBottom: '1px solid color-mix(in srgb, var(--sevv-warning, #f39c12) 30%, transparent)'
        }}>
          <Typography variant="h6" sx={{ color: 'var(--sevv-text-primary, #424242)', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Payment sx={{ color: 'var(--sevv-warning, #f39c12)' }} />
            Confirm Payment
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'color-mix(in srgb, var(--sevv-warning, #f39c12) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--sevv-warning, #f39c12) 30%, transparent)' }}>
            <Typography variant="body2" sx={{ color: 'var(--sevv-text-primary, #424242)' }}>
              You will be charged <strong>{formatCurrency(remainingAfterCredits)}</strong> for adding {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}.
              {useCredits && creditBalance > 0 && (
                <span> {formatCurrency(Math.min(creditBalance, totalAmount))} will be deducted from your credits.</span>
              )}
            </Typography>
          </Alert>
          
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowConfirmation(false)}
              sx={{ 
                color: 'var(--sevv-warning, #f39c12)', 
                borderColor: 'var(--sevv-warning, #f39c12)',
                borderWidth: '2px',
                '&:hover': {
                  borderColor: 'var(--sevv-warning, #f39c12)',
                  backgroundColor: 'color-mix(in srgb, var(--sevv-warning, #f39c12) 10%, transparent)',
                  borderWidth: '2px'
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {pendingUsers.length > 0 && (
        <DialogActions sx={{ 
          backgroundColor: 'var(--sevv-bg-default, #fafafa)', 
          borderTop: '1px solid var(--sevv-divider, #eeeeee)',
          padding: '12px 16px',
          gap: 1
        }}>
          <Button 
            onClick={onClose}
            variant="outlined"
            size="small"
            sx={{ 
              color: 'var(--sevv-primary, #f97415)', 
              borderColor: 'var(--sevv-primary, #f97415)',
              borderWidth: '2px',
              '&:hover': {
                borderColor: 'var(--sevv-primary-light, #ffa047)',
                backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)',
                borderWidth: '2px'
              }
            }}
          >
            Close
          </Button>
          <Button 
            onClick={handleProcessPayment}
            variant="contained"
            size="small"
            disabled={processing || getActionButtonProps().disabled}
            startIcon={processing ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : getActionButtonProps().icon}
            sx={{ 
              background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 500,
              textTransform: 'none',
              minHeight: '36px',
              boxShadow: '0 4px 14px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
              '&:hover': { 
                background: 'linear-gradient(135deg, var(--sevv-primary-dark, #ea580c) 0%, var(--sevv-primary, #f97415) 100%)',
                boxShadow: '0 10px 25px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent)',
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                background: 'color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
                color: 'var(--sevv-gray-400, #bdbdbd)',
                boxShadow: 'none',
                transform: 'none'
              }
            }}
          >
            {processing ? 'Processing...' : getActionButtonProps().text}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
